import React from 'react';
import PropTypes from 'prop-types';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { connect } from 'react-redux';
import Alert from 'react-s-alert';

// css for alerts, needs to be imported as global not through css-modules
import 'react-s-alert/dist/s-alert-default.css';

import AlertTemplate from '../alert/AlertTemplate';
import Overlay from './Overlay';
import LockDownRequirementsFailed from './LockDownRequirementsFailed';
import InvisibleNextButton from '../invisible/InvisibleNextButton';
import rangyService from '../../services/RangyService';
import SelectionContextMenu from '../selectionContextMenu/SelectionContextMenu';
import textToSpeechService from '../../services/TextToSpeechService';
import uiService from '../../services/UIService';
import { setDocumentLanguage } from '../../services/i18nService';
import VideoLivestreamPlaceholder from '../testOverview/testComponents/hardwareAccessCheck/VideoLivestreamPlaceholder';

import styles from './App.scss';
import studentHighlightsService from '../../services/StudentHighlightsService';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import storeManager from '../../store/StoreManager';
import AppMainScreen from './AppMainScreen';
import annotationService from '../../services/AnnotationService';
import liveSessionService from '../../services/livesession/LiveSessionService';
import a11yService from '../../services/a11yService';

import UIConstants, { contrastOptions } from '../../constants/UIConstants';
import appSelector from '../../selectors/app/AppSelector';
import ModalsContainer from '../modal/ModalsContainer';
import FullScreenIframeSwitcher from '../iframe/FullScreenIframeSwitcher';

class AppWrapper extends React.Component {
    static propTypes = {
        catTriggerInProgress: PropTypes.bool,
        children: PropTypes.object,
        isNoteTakingAndHighlightingEnabled: PropTypes.bool.isRequired,
        isIOS: PropTypes.bool.isRequired,
        isStimulusHighlightingEnabled: PropTypes.bool.isRequired,
        addTransition: PropTypes.bool.isRequired,
        showInvisibleNextButton: PropTypes.bool.isRequired,
        showSelectionContextMenu: PropTypes.bool.isRequired,
        needsWebcam: PropTypes.bool.isRequired,
        classList: PropTypes.arrayOf(PropTypes.string).isRequired,
        isPaused: PropTypes.bool.isRequired,
        isPwaChromeApp: PropTypes.bool,
        liveSessionConnectionStatus: PropTypes.string,
        passesLockDownRequirements: PropTypes.bool,
        session: PropTypes.object,
        shouldShowLiveSessionCall: PropTypes.bool.isRequired,
        time: PropTypes.object,
        ui: PropTypes.object,
    };

    componentDidMount() {
        const { session, isNoteTakingAndHighlightingEnabled, isIOS } = this.props;

        this.themeSet = false;
        setDocumentLanguage(session.language.substring(0, 2));
        uiService.setTestPlayerStyle();
        uiService.setCurrentViewSize(this.props.ui);
        if (isIOS) {
            uiService.setScreenIOSOrientation();
            document.body.style.height = `${window.innerHeight}px`;
        }
        window.addEventListener('resize', () => {
            uiService.setCurrentViewSize(this.props.ui);
            if (isIOS) {
                // orientationchange prefers a 200ms timeout
                setTimeout(() => {
                    const ui = storeManager.getState().ui;
                    const currentOrientation = uiService.getCurrentIOSOrientation();
                    if (ui.orientation !== currentOrientation) {
                        document.body.style.height = `${window.innerHeight}px`;
                        uiService.setScreenIOSOrientation();
                    }
                }, 200);
            }
        });

        window.addEventListener('beforeunload', uiService.beforeUnloadHandler);

        document.addEventListener('selectionchange', this.onSelectionChange, false);

        if (isNoteTakingAndHighlightingEnabled) {
            annotationService.init();
            a11yService.initFocusTrap();
        }
    }

    componentDidUpdate(prevProps) {
        if (
            settingsReader.getTestPlayerStyle() !== settingsReader.getTestPlayerStyle(prevProps)
        ) {
            uiService.setTestPlayerStyle();
        }

        const { isNoteTakingAndHighlightingEnabled, ui: { contrastOption = contrastOptions.NORMAL } } = this.props;
        if (prevProps.isNoteTakingAndHighlightingEnabled !== isNoteTakingAndHighlightingEnabled) {
            if (isNoteTakingAndHighlightingEnabled) {
                annotationService.init();
            } else {
                annotationService.destroy();
            }
        }


        if (prevProps.ui.contrastOption !== contrastOption || !this.themeSet) {
            this.setThemeClassToBody(contrastOption);
        }
        liveSessionService.handleLiveSessionConnectionAlert(prevProps.liveSessionConnectionStatus, this.props.liveSessionConnectionStatus);
    }

    componentWillUnmount() {
        document.removeEventListener('selectionchange', this.onSelectionChange, false);
    }

    setThemeClassToBody(contrastOption) {
        const bodyClassList = document.querySelector('body').classList;
        const updatedClassNames = Array.from(bodyClassList).filter(item => !UIConstants.CONTRAST_OPTIONS.includes(item));
        updatedClassNames.push(contrastOption);

        document.querySelector('body').className = updatedClassNames.join(' ');
        this.themeSet = true;
    }

    setHighlightTooltip = (event) => {
        const rangy = rangyService.getRangy();
        const selectionIsMade = window.getSelection().toString();
        const targetIsHighlight = event.target && event.target.className === 'student-highlight';

        if (selectionIsMade && !targetIsHighlight) {
            return studentHighlightsService.updateToolTipBasedOnCurrentSelection();
        } else if (targetIsHighlight) {
            const highlightId = event.target.getAttribute('data-highlight-id');
            const highlightElements = Array.from(
                document.querySelectorAll(`[data-highlight-id="${highlightId}"]`)
            );

            const range = rangy.createRangyRange();
            range.setStartBefore(highlightElements[0]);
            range.setEndAfter(highlightElements[highlightElements.length - 1]);

            const selection = rangy.getSelection();
            selection.setSingleRange(range);

            return studentHighlightsService.updateToolTipBasedOnCurrentSelection();
        }

        return studentHighlightsService.closeTooltip();
    };

    onSelectionChange = (event) => {
        const { isStimulusHighlightingEnabled } = this.props;
        if (this.selectionEndTimeout) {
            clearTimeout(this.selectionEndTimeout);
        }
        this.selectionEndTimeout = setTimeout(() => {
            textToSpeechService.verifySelection(event);
            if (isStimulusHighlightingEnabled) {
                this.setHighlightTooltip(event);
            }
        }, 500);
    };

    render() {
        const { time, children, passesLockDownRequirements, addTransition, showInvisibleNextButton, showSelectionContextMenu,
            shouldShowLiveSessionCall, isPaused, isPwaChromeApp, catTriggerInProgress, classList, needsWebcam } = this.props;

        if (!passesLockDownRequirements) {
            return <LockDownRequirementsFailed />;
        }

        return (
            <div
                className={`${styles.app} ${classList.join(' ')}`}
                id="app"
                role="presentation"
            >
                { addTransition &&
                    <TransitionGroup component={null}>
                        <CSSTransition
                            classNames="fade"
                            timeout={{ enter: 100, exit: 1000 }}
                        >
                            <Overlay
                                pauseTime={isPaused && time.pauseTime}
                                hasBackgroundAudio={assessmentReader.hasBackgroundAudioResource()}
                                shouldShowLiveSessionCall={shouldShowLiveSessionCall}
                                catTriggerInProgress={catTriggerInProgress}
                            />
                        </CSSTransition>
                    </TransitionGroup>
                }

                { isPwaChromeApp &&
                    <FullScreenIframeSwitcher />
                }

                <AppMainScreen isAreaHidden={addTransition}>
                    { children}
                </AppMainScreen>

                <ModalsContainer />
                <Alert stack={{ limit: 3, spacing: 1 }} contentTemplate={AlertTemplate} />

                { needsWebcam && <VideoLivestreamPlaceholder />}
                { showSelectionContextMenu && <SelectionContextMenu /> }
                { showInvisibleNextButton && <InvisibleNextButton /> }
            </div>
        );
    }
}

const mapStateToProps = state => appSelector.selectProps(state);
export default connect(mapStateToProps)(AppWrapper);
